/**
 * React Query hooks for the Data Agent session API.
 *
 * Provides:
 * - useCreateWorkspaceSession — mutation to start a new session
 * - useWorkspaceSession       — query for session metadata
 * - useSendWorkspaceMessage   — mutation to send a user message
 * - useWorkspaceMessages      — query for message history
 * - useWorkspaceOperations    — query for operation ledger
 * - useWorkspaceTables    — query for current workspace tables
 * - useCloseWorkspaceSession  — mutation to close a session
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from 'react-query';
import {
  createWorkspaceSession,
  getWorkspaceSession,
  getActiveSessionForBatch,
  sendWorkspaceMessage,
  getWorkspaceMessages,
  getWorkspaceOperations,
  getWorkspaceTables,
  getWorkspaceStatus,
  closeWorkspaceSession,
  promoteOperation,
  discardOperation,
  previewWorkspaceTable,
  updateOperationExploratory,
  renameOperation,
  autoClassifyOperations,
  queryWorkspace,
  profileWorkspaceColumn,
  profileWorkspaceColumnFromSql,
  createOperation,
  updateSessionMeta,
  getSessionPipelineSteps,
} from '../services/workspaceApi';
import type { PersistedPipelineStep } from '../services/workspaceApi';
import {
  applyProgressEvent,
  initialProgressState,
  snapshotProgressState,
} from '../utils/progressReducer';
import type { ProgressEvent, ProgressState } from '../types/progress';
import type {
  WorkspaceSession,
  WorkspaceMessage,
  WorkspaceMessageResult,
  WorkspaceOperation,
  WorkspaceStreamEvent,
  WorkspaceTable,
  TablePreview,
  CreateSessionParams,
  WorkspaceQueryResult,
  WorkspaceColumnProfile,
  CreateOperationParams,
  UpdateSessionMetaResult,
  WorkspaceStatus,
} from '../services/workspaceApi';
import { buildUrl } from '../services/apiClient';
import { createResilientEventSource } from '../utils/resilientEventSource';

// ── Session lifecycle ────────────────────────────────────────────────

export function useCreateWorkspaceSession(): UseMutationResult<WorkspaceSession, Error, CreateSessionParams> {
  const queryClient = useQueryClient();
  return useMutation(
    (params: CreateSessionParams) => createWorkspaceSession(params),
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['agent-session', data.session_id], data);
      },
    },
  );
}

export function useWorkspaceSession(
  sessionId: string | null,
  options?: { sseConnected?: boolean },
): UseQueryResult<WorkspaceSession> {
  return useQuery(
    ['agent-session', sessionId],
    () => getWorkspaceSession(sessionId!),
    {
      enabled: !!sessionId,
      refetchOnWindowFocus: false,
      // workspace_meta updates (intent, mapping status) are NOT covered by SSE
      // events, so we keep a slow poll even when SSE is connected.  When SSE is
      // disconnected we poll more aggressively.
      refetchInterval: options?.sseConnected ? 30_000 : 5000,
      refetchIntervalInBackground: false,
      retry: false,
    },
  );
}

/**
 * Recover the most recent active session for a batch (if any).
 * Returns null while loading or when no active session exists.
 */
export function useActiveSessionForBatch(batchId: string | undefined): UseQueryResult<WorkspaceSession | null> {
  return useQuery(
    ['agent-active-session', batchId],
    () => getActiveSessionForBatch(batchId!),
    {
      enabled: !!batchId,
      refetchOnWindowFocus: false,
      retry: false,            // 404 is expected when no session exists
      staleTime: 30_000,       // Don't re-fetch for 30 s
    },
  );
}

/**
 * Non-blocking workspace readiness check.
 * Polls /workspace/status at 2 s intervals until the data-worker workspace
 * is rebuilt, then stops.  Uses a plain setInterval for robustness —
 * react-query's refetchInterval can stall after transient 401s or
 * hot-module-replacement in dev mode.
 */
export function useWorkspaceReady(sessionId: string | null): { ready: boolean } {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setReady(false);
      return;
    }

    const abortController = new AbortController();
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let inflight = false;

    const poll = async () => {
      if (inflight || abortController.signal.aborted) return;
      inflight = true;
      try {
        const result = await getWorkspaceStatus(sessionId, abortController.signal);
        if (!abortController.signal.aborted && typeof result?.ready === 'boolean' && result.ready) {
          setReady(true);
          // Stop polling once workspace is ready
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }
      } catch {
        // Swallow — keep polling (includes AbortError on cleanup)
      } finally {
        inflight = false;
      }
    };

    // Fire immediately, then every 2 s
    poll();
    intervalId = setInterval(poll, 2000);

    return () => {
      abortController.abort();
      if (intervalId) clearInterval(intervalId);
    };
  }, [sessionId]);

  // Reset when sessionId changes
  useEffect(() => {
    setReady(false);
  }, [sessionId]);

  return { ready };
}

export function useCloseWorkspaceSession(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation(
    (sessionId: string) => closeWorkspaceSession(sessionId),
    {
      onSuccess: (_data, sessionId) => {
        queryClient.invalidateQueries(['agent-session', sessionId]);
      },
    },
  );
}

// ── Messages ─────────────────────────────────────────────────────────

export type SendMessageInput = string | { message: string; displayText?: string };

export function useSendWorkspaceMessage(sessionId: string | null): UseMutationResult<WorkspaceMessageResult, Error, SendMessageInput> {
  const queryClient = useQueryClient();
  return useMutation(
    (input: SendMessageInput) => {
      const msg = typeof input === 'string' ? input : input.message;
      const dt = typeof input === 'string' ? undefined : input.displayText;
      return sendWorkspaceMessage(sessionId!, msg, dt);
    },
    {
      onSuccess: () => {
        // Refetch messages, operations, and workspace after agent responds
        queryClient.invalidateQueries(['agent-messages', sessionId]);
        queryClient.invalidateQueries(['agent-operations', sessionId]);
        queryClient.invalidateQueries(['agent-workspace', sessionId]);
      },
      onError: () => {
        // Refetch state on error — the agent may have partially completed
        queryClient.invalidateQueries(['agent-messages', sessionId]);
        queryClient.invalidateQueries(['agent-operations', sessionId]);
        queryClient.invalidateQueries(['agent-workspace', sessionId]);
      },
      retry: false,
    },
  );
}

export function useWorkspaceMessages(
  sessionId: string | null,
  options?: { sseConnected?: boolean },
): UseQueryResult<WorkspaceMessage[]> {
  return useQuery(
    ['agent-messages', sessionId],
    () => getWorkspaceMessages(sessionId!),
    {
      enabled: !!sessionId,
      refetchOnWindowFocus: false,
      // When SSE is connected, message_complete events already invalidate
      // this cache.  Keep a slow fallback (30s) for background-job messages
      // that don't flow through the SSE channel.
      refetchInterval: options?.sseConnected ? 30_000 : 8000,
      refetchIntervalInBackground: false,
      retry: false,
    },
  );
}

// ── Operations & Workspace ───────────────────────────────────────────

export function useWorkspaceOperations(
  sessionId: string | null,
  options?: { sseConnected?: boolean },
): UseQueryResult<WorkspaceOperation[]> {
  return useQuery(
    ['agent-operations', sessionId],
    () => getWorkspaceOperations(sessionId!),
    {
      enabled: !!sessionId,
      refetchOnWindowFocus: false,
      // SSE tool_result / message_complete events invalidate this cache,
      // so polling is unnecessary when the stream is live.
      refetchInterval: options?.sseConnected ? false : 5000,
      refetchIntervalInBackground: false,
      retry: false,
    },
  );
}

export function useSessionPipelineSteps(
  sessionId: string | null,
): UseQueryResult<{ pipeline_id: string | null; steps: PersistedPipelineStep[] }> {
  return useQuery(
    ['agent-pipeline-steps', sessionId],
    () => getSessionPipelineSteps(sessionId!),
    {
      enabled: !!sessionId,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      retry: false,
    },
  );
}

export function useWorkspaceTables(
  sessionId: string | null,
  options?: { sseConnected?: boolean },
): UseQueryResult<WorkspaceTable[]> {
  return useQuery(
    ['agent-workspace', sessionId],
    () => getWorkspaceTables(sessionId!),
    {
      enabled: !!sessionId,
      refetchOnWindowFocus: false,
      // SSE tool_result / message_complete events invalidate this cache,
      // so polling is unnecessary when the stream is live.
      refetchInterval: options?.sseConnected ? false : 5000,
      refetchIntervalInBackground: false,
      retry: false,
    },
  );
}

// ── Promote / Discard operations ─────────────────────────────────────

export function usePromoteOperation(
  sessionId: string | null,
): UseMutationResult<{ status: string; operation_id: string }, Error, string> {
  const queryClient = useQueryClient();
  return useMutation(
    (operationId: string) => promoteOperation(sessionId!, operationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['agent-operations', sessionId]);
      },
    },
  );
}

export function useDiscardOperation(
  sessionId: string | null,
): UseMutationResult<{ status: string; operation_id: string }, Error, string> {
  const queryClient = useQueryClient();
  return useMutation(
    (operationId: string) => discardOperation(sessionId!, operationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['agent-operations', sessionId]);
        queryClient.invalidateQueries(['agent-workspace', sessionId]);
      },
    },
  );
}

// ── Data Preview ─────────────────────────────────────────────────────

export function useTablePreview(
  sessionId: string | null,
  tableName: string | null | undefined,
): UseQueryResult<TablePreview> {
  return useQuery(
    ['table-preview', sessionId, tableName],
    () => previewWorkspaceTable(sessionId!, tableName!),
    {
      enabled: !!sessionId && !!tableName,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      retry: false,
    },
  );
}

// ── Exploratory Classification ──────────────────────────────────────

export function useSetOperationExploratory(
  sessionId: string | null,
): UseMutationResult<{ status: string; operation_id: string }, Error, { operationId: string; exploratory: boolean }> {
  const queryClient = useQueryClient();
  return useMutation(
    ({ operationId, exploratory }: { operationId: string; exploratory: boolean }) =>
      updateOperationExploratory(sessionId!, operationId, exploratory),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['agent-operations', sessionId]);
      },
    },
  );
}

export function useRenameOperation(
  sessionId: string | null,
): UseMutationResult<{ status: string; operation_id: string; label: string }, Error, { operationId: string; label: string }> {
  const queryClient = useQueryClient();
  return useMutation(
    ({ operationId, label }: { operationId: string; label: string }) =>
      renameOperation(sessionId!, operationId, label),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['agent-operations', sessionId]);
      },
    },
  );
}

export function useAutoClassifyOperations(
  sessionId: string | null,
): UseMutationResult<{ classified_count: number }, Error, void> {
  const queryClient = useQueryClient();
  return useMutation(
    () => autoClassifyOperations(sessionId!),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['agent-operations', sessionId]);
      },
    },
  );
}

// ── Direct Operation Creation ────────────────────────────────────────

/**
 * Create and execute a transform operation directly from the UI.
 * Invalidates operations, workspace, and messages (synthetic message injected).
 */
export function useCreateOperation(
  sessionId: string | null,
): UseMutationResult<WorkspaceOperation, Error, CreateOperationParams> {
  const queryClient = useQueryClient();
  return useMutation(
    (params: CreateOperationParams) => createOperation(sessionId!, params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['agent-operations', sessionId]);
        queryClient.invalidateQueries(['agent-workspace', sessionId]);
        queryClient.invalidateQueries(['agent-messages', sessionId]);
      },
      retry: false,
    },
  );
}

// ── Session Meta Updates ─────────────────────────────────────────────

/**
 * Update workspace_meta from the UI (schema selection, connector config).
 * Invalidates session data so workspace_meta reflects the change.
 */
export function useUpdateSessionMeta(
  sessionId: string | null,
): UseMutationResult<UpdateSessionMetaResult, Error, Record<string, any>> {
  const queryClient = useQueryClient();
  return useMutation(
    (updates: Record<string, any>) => updateSessionMeta(sessionId!, updates),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['agent-session', sessionId]);
        queryClient.invalidateQueries(['agent-active-session']);
      },
    },
  );
}

// ── Workspace Query & Column Profile ─────────────────────────────────

export function useWorkspaceQuery(
  sessionId: string | null,
): UseMutationResult<WorkspaceQueryResult, Error, {
  sql: string;
  limit?: number;
  offset?: number;
  includeTotalCount?: boolean;
}> {
  return useMutation(
    ({ sql, limit, offset, includeTotalCount }: {
      sql: string;
      limit?: number;
      offset?: number;
      includeTotalCount?: boolean;
    }) => queryWorkspace(sessionId!, sql, limit, offset, includeTotalCount),
  );
}

export function useWorkspaceColumnProfile(
  sessionId: string | null,
  tableName: string | null,
  columnName: string | null,
  querySql?: string | null,
  displayDtype?: string,
): UseQueryResult<WorkspaceColumnProfile> {
  const trimmedSql = querySql?.trim() || '';
  const useQueryProfile = !!trimmedSql;

  return useQuery(
    ['column-profile', sessionId, tableName, columnName, trimmedSql],
    () => {
      if (useQueryProfile) {
        return profileWorkspaceColumnFromSql(
          sessionId!,
          trimmedSql,
          columnName!,
          displayDtype,
        );
      }
      return profileWorkspaceColumn(sessionId!, tableName!, columnName!);
    },
    {
      enabled: !!sessionId && !!columnName && (useQueryProfile || !!tableName),
      staleTime: 120_000,
      refetchOnWindowFocus: false,
    },
  );
}

// ── SSE Streaming ─────────────────────────────────────────────────────

export interface PipelineStepState {
  step_id: string;
  step_type: string;
  title: string;
  status: string;
  step_order: number;
  output_ref?: string;
  input_refs: string[];
  actual_cost?: number;
  row_count?: number;
  /** Step-runner metrics (e.g. assembly emits byte_size, output_format). */
  metrics?: Record<string, any>;
  error?: string;
}

export interface StepProgress {
  rows_processed: number;
  total_rows: number;
  elapsed_seconds: number;
}

export interface PipelineState {
  pipeline_id: string;
  status: string;
  total_steps: number;
  steps: PipelineStepState[];
  total_cost_usd?: number;
  stepProgress?: Record<string, StepProgress>;
}

export interface CostApprovalRequest {
  operationType: string;
  description: string;
  estimatedCostUsd: number;
  rowCount?: number;
  estimatedDurationSec?: number;
}

export interface FinalizationIssue {
  stepId: string;
  stepTitle: string;
  severity: 'warning' | 'error';
  message: string;
  canAutoFix: boolean;
}

export interface WorkspaceStreamState {
  /** Current tool being executed (null when idle) */
  activeTool: string | null;
  /** Whether the review agent is running */
  reviewInProgress: boolean;
  /** Last event received */
  lastEvent: WorkspaceStreamEvent | null;
  /** Whether the stream is connected */
  connected: boolean;
  /** Whether the agent is currently processing a message (server-authoritative) */
  agentBusy: boolean;
  /** Pipeline execution state (updated via SSE events) */
  pipelineState: PipelineState | null;
  /** Pending cost approval request (shown as dialog) */
  costApprovalPending: CostApprovalRequest | null;
  /** Finalization blocking issues (shown as gate modal) */
  finalizationBlocked: FinalizationIssue[] | null;
  /**
   * Unified progress channel. Engine-mode (CoordinatorAgent) and
   * iterative-mode (ToolHandler) both publish ``progress`` events into
   * this state via the shared reducer. The ProgressStrip / ProgressSheet
   * / canvas-node badges render off this single source of truth.
   */
  progress: ProgressState;
}

export function useWorkspaceStream(sessionId: string | null): WorkspaceStreamState {
  const queryClient = useQueryClient();
  const [state, setState] = useState<WorkspaceStreamState>({
    activeTool: null,
    reviewInProgress: false,
    agentBusy: false,
    lastEvent: null,
    connected: false,
    pipelineState: null,
    costApprovalPending: null,
    finalizationBlocked: null,
    progress: initialProgressState(),
  });

  // Initial hydration: seed agentBusy + progress from the REST session
  // response. The working indicator and progress strip both need to be
  // accurate on page load/reload without waiting for the first SSE
  // heartbeat. ``workspace_meta.progress`` is the persisted reducer state
  // owned by ProgressEmitter on the backend.
  //
  // Subscribed via React Query rather than a one-shot ``getQueryData``
  // read so the effect re-runs when the session fetch resolves after
  // the hook mounts (the typical case on a hard reload, where the
  // session query is still in flight when ``useWorkspaceStream`` first
  // runs). Without the subscription, hydration silently skipped on
  // every reload and the progress panel only repopulated once the SSE
  // stream replayed an event.
  const { data: hydrationSession } = useQuery<WorkspaceSession | null>(
    ['agent-session', sessionId],
    () => (sessionId ? getWorkspaceSession(sessionId) : Promise.resolve(null)),
    { enabled: !!sessionId, staleTime: Infinity },
  );
  useEffect(() => {
    if (!sessionId || !hydrationSession) return;
    setState((s) => {
      const next = { ...s };
      if (hydrationSession.agent_busy) next.agentBusy = true;
      const persisted = (hydrationSession.workspace_meta as { progress?: ProgressState } | undefined)
        ?.progress;
      if (persisted && Array.isArray(persisted.phases)) {
        next.progress = snapshotProgressState(persisted);
      }
      return next;
    });
  }, [sessionId, hydrationSession]);

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, connected: false }));
      return;
    }

    const handle = createResilientEventSource({
      // Auth is carried on the HttpOnly cookie — resilientEventSource sets
      // `withCredentials: true` so no token parameter is required.
      urlBuilder: () => buildUrl(`/api/v1/agents/sessions/${sessionId}/stream`),
      onConnected: () => {
        setState((s) => ({ ...s, connected: true }));
      },
      onDisconnected: () => {
        setState((s) => ({ ...s, connected: false }));
      },
      onReconnected: () => {
        setState((s) => ({ ...s, connected: true }));
        // Invalidate all workspace caches to catch events missed during disconnect
        queryClient.invalidateQueries(['agent-session', sessionId]);
        queryClient.invalidateQueries(['agent-messages', sessionId]);
        queryClient.invalidateQueries(['agent-operations', sessionId]);
        queryClient.invalidateQueries(['agent-workspace', sessionId]);
      },
      eventListeners: {
        'agent_event': (e: MessageEvent) => {
          try {
            const event: WorkspaceStreamEvent = JSON.parse(e.data);
            setState((s) => {
              const next = {
                ...s,
                lastEvent: event,
                activeTool: event.type === 'tool_start' ? event.data.tool : event.type === 'tool_result' ? null : s.activeTool,
                reviewInProgress: event.type === 'review_start' ? true : event.type === 'review_result' ? false : s.reviewInProgress,
              };

              // Pipeline execution events
              if (event.type === 'pipeline_start') {
                const stepIds: string[] = event.data.step_ids || [];
                next.pipelineState = {
                  pipeline_id: event.data.pipeline_id || '',
                  status: 'running',
                  total_steps: event.data.total_steps || stepIds.length,
                  steps: stepIds.map((id: string, i: number) => ({
                    step_id: id,
                    step_type: '',
                    title: '',
                    status: 'pending',
                    step_order: i,
                    input_refs: [],
                  })),
                };
              } else if (event.type === 'step_start' && next.pipelineState) {
                next.pipelineState = {
                  ...next.pipelineState,
                  steps: next.pipelineState.steps.map((step) =>
                    step.step_id === event.data.step_id
                      ? { ...step, status: 'running', step_type: event.data.step_type || step.step_type, title: event.data.title || step.title }
                      : step,
                  ),
                };
              } else if (event.type === 'step_complete' && next.pipelineState) {
                next.pipelineState = {
                  ...next.pipelineState,
                  steps: next.pipelineState.steps.map((step) =>
                    step.step_id === event.data.step_id
                      ? {
                          ...step,
                          status: event.data.status || 'completed',
                          output_ref: event.data.output_ref,
                          error: event.data.error,
                          row_count: event.data.row_count ?? step.row_count,
                          metrics: event.data.metrics ?? step.metrics,
                        }
                      : step,
                  ),
                };
              } else if (event.type === 'pipeline_complete' && next.pipelineState) {
                next.pipelineState = {
                  ...next.pipelineState,
                  status: 'completed',
                  total_cost_usd: event.data.total_cost_usd,
                };
              } else if (event.type === 'pipeline_failed' && next.pipelineState) {
                next.pipelineState = {
                  ...next.pipelineState,
                  status: 'failed',
                };
              } else if (event.type === 'pipeline_paused' && next.pipelineState) {
                next.pipelineState = {
                  ...next.pipelineState,
                  status: 'paused',
                };
              } else if (event.type === 'step_progress' as any && next.pipelineState) {
                next.pipelineState = {
                  ...next.pipelineState,
                  stepProgress: {
                    ...next.pipelineState.stepProgress,
                    [event.data.step_id]: {
                      rows_processed: event.data.rows_processed ?? 0,
                      total_rows: event.data.total_rows ?? 0,
                      elapsed_seconds: event.data.elapsed_seconds ?? 0,
                    },
                  },
                };
              }

              // Unified progress channel — engine + iterative both
              // publish here. The reducer applies the event to a fresh
              // snapshot so React notices the change; the persisted
              // version on the backend is the same shape so reload
              // hydration "just works".
              if (event.type === 'progress' as any) {
                const progressEvent = event.data as ProgressEvent;
                const updated = applyProgressEvent(
                  snapshotProgressState(s.progress),
                  progressEvent,
                );
                next.progress = updated;
              }

              // Agent busy status (server-authoritative busy flag)
              if (event.type === 'agent_status' as any) {
                next.agentBusy = event.data.busy ?? false;
                if (!event.data.busy) {
                  next.activeTool = null;
                }
              }

              // Cost approval and finalization events
              if (event.type === 'cost_approval_required' as any) {
                next.costApprovalPending = {
                  operationType: event.data.operation_type || '',
                  description: event.data.description || '',
                  estimatedCostUsd: event.data.estimated_cost_usd || 0,
                  rowCount: event.data.row_count,
                  estimatedDurationSec: event.data.estimated_duration_sec,
                };
              }
              if (event.type === 'finalization_blocked' as any) {
                next.finalizationBlocked = (event.data.issues || []).map((issue: any) => ({
                  stepId: issue.step_id || '',
                  stepTitle: issue.step_title || '',
                  severity: issue.severity || 'warning',
                  message: issue.message || '',
                  canAutoFix: issue.can_auto_fix || false,
                }));
              }

              return next;
            });

            // Invalidate queries on relevant events
            if (event.type === 'tool_result' || event.type === 'review_result') {
              queryClient.invalidateQueries(['agent-operations', sessionId]);
              queryClient.invalidateQueries(['agent-workspace', sessionId]);
              queryClient.invalidateQueries(['agent-pipeline-steps', sessionId]);
              const tool = event.type === 'tool_result' ? event.data.tool : undefined;
              if (tool === 'select_target_schema' || tool === 'map_to_schema') {
                queryClient.invalidateQueries(['agent-active-session']);
              }
            }
            if (event.type === 'message_complete') {
              queryClient.invalidateQueries(['agent-messages', sessionId]);
              queryClient.invalidateQueries(['agent-operations', sessionId]);
              queryClient.invalidateQueries(['agent-workspace', sessionId]);
              queryClient.invalidateQueries(['agent-pipeline-steps', sessionId]);
              queryClient.invalidateQueries(['agent-session', sessionId]);
              queryClient.invalidateQueries(['agent-active-session']);
            }
            if (event.type === 'pipeline_complete' || event.type === 'pipeline_failed' || event.type === 'step_complete') {
              queryClient.invalidateQueries(['agent-pipeline-steps', sessionId]);
            }
            // Re-broadcast readiness-resolution-applied events as a
            // window CustomEvent so any readiness host (assembly
            // inspector, review pane, outputs drawer) can refresh
            // reactively. Hosts may live outside this hook's component
            // subtree, so a window-level fan-out is the simplest
            // cross-tree contract — no extra EventSource per host, no
            // global context plumbing.
            if ((event.type as string) === 'readiness_resolution_applied') {
              try {
                window.dispatchEvent(
                  new CustomEvent('readiness:resolution-applied', {
                    detail: { session_id: sessionId, ...event.data },
                  }),
                );
              } catch {
                // Older browsers — ignore; the hosts' existing
                // focus/visibility refresh covers the gap.
              }
            }
            // agent_status busy=false means the agent just finished — invalidate
            // caches so the UI picks up results without waiting for the next poll.
            // This covers the case where message_complete is not explicitly emitted.
            if ((event.type as string) === 'agent_status' && !event.data.busy) {
              queryClient.invalidateQueries(['agent-messages', sessionId]);
              queryClient.invalidateQueries(['agent-operations', sessionId]);
              queryClient.invalidateQueries(['agent-workspace', sessionId]);
              queryClient.invalidateQueries(['agent-session', sessionId]);
              queryClient.invalidateQueries(['agent-active-session']);
            }
          } catch {
            // Ignore parse errors
          }
        },
        'heartbeat': (e: MessageEvent) => {
          try {
            const data = JSON.parse(e.data);
            setState((s) => ({
              ...s,
              connected: true,
              agentBusy: data.busy ?? s.agentBusy,
            }));
          } catch {
            setState((s) => ({ ...s, connected: true }));
          }
        },
      },
    });

    return () => {
      handle.close();
      setState({ activeTool: null, reviewInProgress: false, agentBusy: false, lastEvent: null, connected: false, pipelineState: null, costApprovalPending: null, finalizationBlocked: null, progress: initialProgressState() });
    };
  }, [sessionId, queryClient]);

  return state;
}
